import { useEffect, useMemo } from "react";
import { useLazyQuery } from "@apollo/react-hooks";
import { isEmpty } from "lodash";

import useSetState from "core/hooks/useSetState";
import { useAppState } from "core/contexts/AppContext";
import { getCanRenderWebp } from "core/includes/image";
import { productListQuery, commonProductDataQuery } from "core/includes/queries";

import {
    PRODUCT_PAGE_TYPE,
    PRODUCT_SUITABILITY_INIT_OPTION,
    PRODUCT_GROUP_INIT_OPTION,
} from "pages/Components/ProductListPage/includes/constants";

type DefaultState = {
    hasCompletedProductListQuery: boolean,
}

const INITIAL_STATE = {
    hasCompletedProductListQuery: false,
}

const useProductListPageData = (
    pageData: ProductListPage,
    minPriceRange?: number,
    maxPriceRange?: number,
    productGroup?: Option,
    productSuitability?: Option,
    sortField?: string,
    sortDirection?: string,
) => {
    const [state, setState] = useSetState<DefaultState>(INITIAL_STATE);
    const [appState] = useAppState();
    const canRenderWebp = getCanRenderWebp();

    const country = useMemo(() => {
        const country = appState.countryCode?.code.toLowerCase() || '';
        return (isEmpty(country) || country === 'other') ? 'nz' : country;
    }, [appState.countryCode?.code]);

    const [loadCommonData, { data: commonData }] = useLazyQuery(commonProductDataQuery);
    const [loadData, { data, loading }] = useLazyQuery(productListQuery);

    useEffect(() => {
        setState({ hasCompletedProductListQuery: false });

        if (appState.countryCode?.code) {
            loadCommonData({
                variables: {
                    type: PRODUCT_PAGE_TYPE,
                    parentId: pageData?.id,
                },
                notifyOnNetworkStatusChange: true,
                fetchPolicy: "network-only",
            });

            loadData({
                variables: {
                    parentId: pageData?.id,
                    minPriceRange: minPriceRange,
                    maxPriceRange: maxPriceRange,
                    productGroup: productGroup?.value || PRODUCT_GROUP_INIT_OPTION.value,
                    suitability: productSuitability?.value || PRODUCT_SUITABILITY_INIT_OPTION.value,
                    countryCode: country,
                    sortField: sortField,
                    sortDirection: sortDirection,
                    canRenderWebp,
                },
                notifyOnNetworkStatusChange: true,
                fetchPolicy: "network-only",
                onCompleted: () => setState({ hasCompletedProductListQuery: true }),
            });
        }
    }, [
        appState.countryCode?.code,
        loadCommonData,
        loadData,
        pageData?.id,
        minPriceRange,
        maxPriceRange,
        productGroup?.value,
        productSuitability?.value,
        country,
        sortField,
        sortDirection,
        canRenderWebp,
        setState
    ]);

    const productList = data?.productList;

    const productGroupOptions = [PRODUCT_GROUP_INIT_OPTION];
    commonData?.productGroup.forEach((item: ProductGroupItem) => {
        productGroupOptions.push({ value: item.id, label: item.name });
    });

    const productSuitabilityOptions = [PRODUCT_SUITABILITY_INIT_OPTION];
    commonData?.productSuitability.forEach((item: ProductSuitabilityItem) => {
        productSuitabilityOptions.push({ value: item.id, label: item.name });
    });

    const countryCode = data?.countryCodes.find((codeItem: CountryCodeItem) => codeItem.code === appState.countryCode.code);

    return {
        productList,
        loading,
        productGroupOptions,
        productSuitabilityOptions,
        countryCode: countryCode || '',
        hasCompletedProductListQuery: state.hasCompletedProductListQuery,
    };
};

export default useProductListPageData;
